/** Triage action buttons for one finding (GIS Phase 2A, Task 10, spec §3's
 *  action matrix). Rendered from the finding's `discrepancy_type` via
 *  `actionsForType` (types.ts) — a type-mismatched action never even shows
 *  up as a button, though the server still enforces it (409) as the last
 *  line of defense (spec §2: "UI hides what the user lacks, but hiding is
 *  cosmetic").
 *
 *  Every action needs `gis.triage` (the route's own gate); the six
 *  entity-mutating actions additionally need `gis.override`. A user missing
 *  either sees the button disabled with a tooltip NAMING the missing
 *  capability — never a silently-vanished action.
 *
 *  Actions that require a note and/or a target parent id (dismiss,
 *  resolve_identity_keep_owner, keep_platform_relink, confirm_retire,
 *  assign_parent, propose_parent) open an inline confirm form instead of
 *  firing immediately; everything else is a single click with an explicit
 *  outcome reported back to the caller (UX-review Stage-1: plain verbs,
 *  explicit outcomes, no silent failures).
 *
 *  adopt_gis_value (post-2A user-mandated addition, spec §3, 2026-07-28)
 *  requires neither a note nor a target parent, but ALSO opens the inline
 *  form (`ActionSpec.requiresValueConfirmation`) — the write it performs is
 *  consequential enough (any syncable attribute, meter_number included)
 *  that the operator must see the explicit before/after stated in plain
 *  language before confirming, mirroring the "no silent failures" rule for
 *  the write itself, not just its outcome. For `meter_number` specifically
 *  the confirm copy also names the operational consequence — AMI matching
 *  and outage resolution both key on this column (task-5(AMI) review M2) —
 *  since a wrong adopt here is not just a data edit but a billing/outage
 *  attribution change. A 409 (the export is known to carry duplicate meter
 *  numbers) surfaces through the same generic error path every other
 *  action's failure does — `friendlyError` surfaces `err.detail` for any
 *  4xx ApiError, so no action-specific handling is needed.
 *
 *  Task 11a integration note (target_parent_id): the manual numeric input
 *  below was Task 10's stopgap for assign_parent/propose_parent's required
 *  `target_parent_id`. `EvidencePanel`'s candidate table (Task 11a) is now
 *  the PREFERRED path — `FindingRow` lifts the candidate table's selection
 *  up as `selectedTargetParentId` and passes it here. The manual input is
 *  KEPT, not removed: it is the documented fallback for a target the
 *  evidence endpoint's candidate search didn't surface (the nearest 5
 *  within 500 m, spec §5 — an operator may know the correct id lies outside
 *  that window). Opening the form for a target-requiring action seeds the
 *  field from the current selection, and a `useEffect` keyed on
 *  `selectedTargetParentId` re-syncs it whenever the operator picks a
 *  DIFFERENT candidate (in the table or by clicking its mini-map marker) —
 *  last selection wins. The operator can still hand-edit the field
 *  afterward; it is only overwritten by a NEW selection, never by a
 *  keystroke of its own. */

import { useEffect, useState } from 'react'
import { actOnFinding } from './api.ts'
import { friendlyError } from '../../utils/friendlyError.ts'
import {
  ACTION_SPECS,
  actionsForType,
  hasCapability,
  type Finding,
  type FindingAction,
  type FindingActionResponse,
} from './types.ts'

interface ActionButtonsProps {
  finding: Finding
  capabilities: readonly string[]
  busy: boolean
  onBusyChange: (busy: boolean) => void
  onSuccess: (response: FindingActionResponse) => void
  onError: (message: string) => void
  /** The evidence panel's currently-selected candidate `entity_id` (Task
   *  11a) — seeds/re-syncs the target-parent-id field for assign_parent/
   *  propose_parent. Null/undefined when no candidate is selected (e.g. no
   *  evidence loaded yet, or this finding type has none), in which case the
   *  manual input behaves exactly as it did before this task. */
  selectedTargetParentId?: number | null
}

export function ActionButtons({
  finding,
  capabilities,
  busy,
  onBusyChange,
  onSuccess,
  onError,
  selectedTargetParentId = null,
}: ActionButtonsProps) {
  const [openAction, setOpenAction] = useState<FindingAction | null>(null)
  const [note, setNote] = useState('')
  const [targetParentId, setTargetParentId] = useState('')

  const actions = actionsForType(finding.discrepancy.discrepancy_type)

  // Re-sync the target field whenever the operator picks a NEW candidate
  // while a target-requiring action's form is open — see the module
  // docstring's "Task 11a integration note". Does not fire on every
  // keystroke: `targetParentId` is deliberately not a dependency.
  useEffect(() => {
    if (!openAction) return
    if (!ACTION_SPECS[openAction].requiresTargetParent) return
    setTargetParentId(selectedTargetParentId != null ? String(selectedTargetParentId) : '')
  }, [selectedTargetParentId, openAction])

  function missingCapabilityFor(action: FindingAction): string | null {
    if (!hasCapability(capabilities, 'gis.triage')) return 'gis.triage'
    const spec = ACTION_SPECS[action]
    if (spec.additionalCapability && !hasCapability(capabilities, spec.additionalCapability)) {
      return spec.additionalCapability
    }
    return null
  }

  function closeForm() {
    setOpenAction(null)
    setNote('')
    setTargetParentId('')
  }

  async function submit(action: FindingAction, noteValue: string, targetValue: string) {
    const spec = ACTION_SPECS[action]
    onBusyChange(true)
    try {
      const body: { action: FindingAction; note?: string; target_parent_id?: number } = {
        action,
      }
      if (spec.requiresNote) body.note = noteValue.trim()
      if (spec.requiresTargetParent) body.target_parent_id = Number(targetValue)
      const response = await actOnFinding(finding.discrepancy.id, body)
      onSuccess(response)
      closeForm()
    } catch (err) {
      onError(friendlyError(err, 'apply this action'))
    } finally {
      onBusyChange(false)
    }
  }

  function handleClick(action: FindingAction) {
    const spec = ACTION_SPECS[action]
    if (openAction === action) {
      closeForm()
      return
    }
    if (spec.requiresNote || spec.requiresTargetParent || spec.requiresValueConfirmation) {
      setOpenAction(action)
      setNote('')
      setTargetParentId(
        spec.requiresTargetParent && selectedTargetParentId != null ? String(selectedTargetParentId) : '',
      )
      return
    }
    void submit(action, '', '')
  }

  if (actions.length === 0) {
    return (
      <div className="text-xs text-gray-400">No triage action is defined for this finding type.</div>
    )
  }

  const openSpec = openAction ? ACTION_SPECS[openAction] : null
  const noteValid = !openSpec?.requiresNote || note.trim().length > 0
  const noteWithinLimit = note.length <= 2000
  const targetValid =
    !openSpec?.requiresTargetParent || (targetParentId.trim() !== '' && Number(targetParentId) > 0)
  const canConfirm = noteValid && noteWithinLimit && targetValid && !busy

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5" role="group" aria-label="Triage actions">
        {actions.map(action => {
          const spec = ACTION_SPECS[action]
          const missing = missingCapabilityFor(action)
          const disabled = !!missing || busy
          return (
            <button
              key={action}
              type="button"
              onClick={() => handleClick(action)}
              disabled={disabled}
              title={missing ? `Requires the '${missing}' capability` : undefined}
              aria-pressed={openAction === action}
              className={`text-xs px-2.5 py-1 rounded disabled:opacity-50 disabled:cursor-not-allowed ${
                openAction === action
                  ? 'bg-blue-600 text-white'
                  : 'bg-blue-50 text-blue-700 hover:bg-blue-100'
              }`}
            >
              {spec.label}
            </button>
          )
        })}
      </div>

      {openAction && openSpec && (
        <div className="rounded-md border border-gray-200 bg-gray-50 p-2.5 space-y-2">
          {openSpec.requiresValueConfirmation && (
            <p className="text-xs text-gray-700">
              Platform&rsquo;s <span className="font-mono">{finding.discrepancy.field_name}</span> will change from{' '}
              <span className="font-mono">{finding.discrepancy.platform_value ?? '(empty)'}</span> to{' '}
              <span className="font-mono">{finding.discrepancy.gis_value ?? '(empty)'}</span>.
              {finding.discrepancy.field_name === 'meter_number' &&
                ' Changing a meter number affects AMI matching and outage resolution.'}
            </p>
          )}
          {openSpec.requiresNote && (
            <div>
              <label
                htmlFor={`note-${finding.discrepancy.id}-${openAction}`}
                className="block text-xs font-medium text-gray-600 mb-1"
              >
                Note (required)
              </label>
              <textarea
                id={`note-${finding.discrepancy.id}-${openAction}`}
                value={note}
                onChange={e => setNote(e.target.value)}
                maxLength={2000}
                rows={2}
                className="w-full text-xs border border-gray-300 rounded px-2 py-1"
                placeholder="Explain the decision — required for the audit trail."
              />
            </div>
          )}
          {openSpec.requiresTargetParent && (
            <div>
              <label
                htmlFor={`target-${finding.discrepancy.id}-${openAction}`}
                className="block text-xs font-medium text-gray-600 mb-1"
              >
                Target parent ID (required)
              </label>
              <input
                id={`target-${finding.discrepancy.id}-${openAction}`}
                type="number"
                min={1}
                value={targetParentId}
                onChange={e => setTargetParentId(e.target.value)}
                className="w-32 text-xs border border-gray-300 rounded px-2 py-1"
                placeholder="e.g. 4213"
              />
              {selectedTargetParentId != null && String(selectedTargetParentId) === targetParentId ? (
                <p className="text-xs text-gray-500 mt-1">From the candidate selected above.</p>
              ) : (
                <p className="text-xs text-gray-500 mt-1">
                  Not in the candidate list above? Enter the id directly.
                </p>
              )}
            </div>
          )}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => void submit(openAction, note, targetParentId)}
              disabled={!canConfirm}
              className="text-xs px-2.5 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Confirm {openSpec.confirmVerb}
            </button>
            <button
              type="button"
              onClick={closeForm}
              disabled={busy}
              className="text-xs px-2.5 py-1 rounded bg-white border border-gray-300 text-gray-700 hover:bg-gray-100"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
